import { useTheme } from "@/components/providers/theme-provider";
import { Button } from "@/components/ui/button";
import { useMindMap, useUpdateMindMap } from "@/hooks/useMindMaps";
import { Excalidraw, THEME } from "@excalidraw/excalidraw";
import { ExcalidrawElement } from "@excalidraw/excalidraw/types/element/types";
import { AppState, BinaryFiles } from "@excalidraw/excalidraw/types/types";
import { format } from "date-fns";
import { Loader2, Save } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import "./excalidraw-styles.css";

interface MindMapEditorProps {
  readOnly?: boolean;
}

const MindMapEditor: React.FC<MindMapEditorProps> = ({ readOnly = false }) => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  // const location = useLocation(); // Unused
  // const navigate = useNavigate(); // Unused
  const excalidrawRef = useRef<any>(null);

  // Determine if the current theme is dark
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      if (theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return ["dark", "midnight", "forest", "dracula"].includes(theme);
    };

    setIsDark(checkDarkMode());

    // Listen for system theme changes if theme is 'system'
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDark(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [initialData, setInitialData] = useState<{
    elements: ExcalidrawElement[];
    appState: Partial<AppState>;
    files: BinaryFiles;
  } | null>(null);

  // Get mind map data


  // Use the React Query hook to fetch mind map data
  const { data: mindMapData, isLoading: isLoadingMindMap } = useMindMap(id);

  useEffect(() => {
    if (mindMapData) {
      setTitle(mindMapData.title);
      setDescription(mindMapData.description || "");
      if (mindMapData.updated_at) {
        setLastSaved(new Date(mindMapData.updated_at));
      }
      if (mindMapData.content) {
        // Create a safe version of app state without collaboration data
        const safeAppState = { ...(mindMapData.content.appState || {}) };
        if (safeAppState.collaborators) {
          delete safeAppState.collaborators;
        }

        setInitialData({
          elements: mindMapData.content.elements || [],
          appState: safeAppState,
          files: mindMapData.content.files || {},
        });
      }
      setLoading(false);
    } else if (!isLoadingMindMap && id) {
      // Handle error or not found
      setLoading(false);
    }
  }, [mindMapData, isLoadingMindMap, id]);

  const { mutate: updateMindMapMutation, isPending: isUpdating } = useUpdateMindMap();

  const saveCurrentMindMap = useCallback(async () => {
    if (!id) {
      toast.error("Invalid mind map ID");
      return;
    }

    setSaving(true);

    try {
      // Get the current Excalidraw content
      const excalidrawContent = excalidrawRef.current?.getSceneElements();
      const appState = excalidrawRef.current?.getAppState();
      const files = excalidrawRef.current?.getFiles();

      // Create a safe version of app state without collaboration data
      const safeAppState = { ...appState };
      if (safeAppState.collaborators) {
        delete safeAppState.collaborators;
      }

      const content = {
        elements: excalidrawContent,
        appState: safeAppState,
        files,
      };

      // Update mind map using mutation
      updateMindMapMutation(
        {
          mindMapId: id,
          title,
          description,
          content,
        },
        {
          onSuccess: (data) => {
            // Update last saved time
            const now = new Date();
            setLastSaved(now);
            toast.success("Mind map saved");
            setSaving(false);
          },
          onError: (error) => {
            console.error("Failed to save mind map:", error);
            toast.error("Failed to save mind map");
            setSaving(false);
          }
        }
      );
    } catch (error) {
      console.error("Failed to prepare mind map data:", error);
      toast.error("Failed to prepare mind map data");
      setSaving(false);
    }
  }, [title, description, id, updateMindMapMutation]);

  // Auto-save at regular intervals
  useEffect(() => {
    if (readOnly) return;

    const autoSaveInterval = setInterval(() => {
      if (excalidrawRef.current) {
        saveCurrentMindMap();
      }
    }, 60000); // Auto-save every minute

    return () => clearInterval(autoSaveInterval);
  }, [saveCurrentMindMap, readOnly]);

  // Keyboard shortcut for saving
  useEffect(() => {
    if (readOnly) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveCurrentMindMap();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveCurrentMindMap, readOnly]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  // Get theme colors for Excalidraw
  const excalidrawTheme = isDark ? THEME.DARK : THEME.LIGHT;

  const renderTopRightUI = () => {
    return (
      <div className="flex items-center gap-2">
        {/* Status Indicator - styled exactly like Excalidraw Island */}
        <div className="flex h-[40px] items-center justify-center rounded-lg bg-[#ffffff] px-3 text-xs font-medium text-black shadow-md dark:bg-[#232329] dark:text-[#ced4da] whitespace-nowrap">
          {lastSaved ? (
            <>Last saved: {format(lastSaved, 'MMM d, h:mm a')}</>
          ) : (
            <>Not saved yet</>
          )}
        </div>

        {!readOnly && (
          <Button
            onClick={saveCurrentMindMap}
            size="sm"
            // Style to match Excalidraw's toolbar buttons (same gray, same height)
            className="h-[40px] gap-2 rounded-lg border-none bg-[#ffffff] text-black shadow-md hover:bg-[#f1f3f5] dark:bg-[#232329] dark:text-[#ced4da] dark:hover:bg-[#2c2c35]"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      {initialData ? (
        <div className="h-full w-full">
          <Excalidraw
            ref={excalidrawRef}
            initialData={{
              elements: initialData.elements,
              appState: {
                ...initialData.appState,
                theme: excalidrawTheme,
                collaborators: new Map(), // Empty Map to prevent errors
              },
              files: initialData.files
            }}
            viewModeEnabled={readOnly}
            theme={excalidrawTheme}
            renderTopRightUI={renderTopRightUI}
            UIOptions={{
              canvasActions: {
                export: false,
                loadScene: false,
                saveToActiveFile: false,
                saveAsImage: false,
                toggleTheme: false,
                clearCanvas: true,
              },
              welcomeScreen: false,
              dockedSidebarBreakpoint: 1000000, // Make sure sidebar is never docked
            }}
          />
          {/* Overlay to block any collaboration UI that might still appear */}
          <div
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '260px',
              height: '100%',
              zIndex: 2,
              backgroundColor: isDark ? '#1e1e2e' : '#ffffff',
              opacity: 0.01,
              pointerEvents: 'none'
            }}
          />
        </div>
      ) : (
        <div className="h-full w-full">
          <Excalidraw
            ref={excalidrawRef}
            viewModeEnabled={readOnly}
            theme={excalidrawTheme}
            renderTopRightUI={renderTopRightUI}
            initialData={{
              appState: {
                theme: excalidrawTheme,
                collaborators: new Map(), // Empty Map to prevent errors
              },
            }}
            UIOptions={{
              canvasActions: {
                export: false,
                loadScene: false,
                saveToActiveFile: false,
                saveAsImage: false,
                toggleTheme: false,
                clearCanvas: true,
              },
              welcomeScreen: false,
              dockedSidebarBreakpoint: 1000000, // Make sure sidebar is never docked
            }}
          />
          {/* Overlay to block any collaboration UI that might still appear */}
          <div
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '260px',
              height: '100%',
              zIndex: 2,
              backgroundColor: isDark ? '#1e1e2e' : '#ffffff',
              opacity: 0.01,
              pointerEvents: 'none'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MindMapEditor;